import React, { FunctionComponent, ReactNode, SVGProps } from 'react';
import Typography from '@mui/material/Typography';
import { Box, SxProps } from '@mui/system';
import SvgIcon from '@mui/material/SvgIcon';
import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import { LaunchOutlined as LaunchIcon, SvgIconComponent } from '@mui/icons-material';
import { Button, Tooltip, Zoom } from '@mui/material';
import StyledLink from '@/components/styled/StyledLink';

export interface SidebarLinkProps {
  to?: string | undefined;
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | SvgIconComponent;
  text?: string;
  boxContainerStyles?: SxProps;
  target?: string | undefined;
  handleClick?: (() => void) | string | undefined;
  isVisible?: boolean;
  isExternal?: boolean;
  subMenu?: boolean;
}

const SidebarLink: React.FC<SidebarLinkProps> = ({
  to = '',
  icon,
  text,
  boxContainerStyles,
  target,
  handleClick,
  isExternal,
  subMenu,
}) => {
  const { sidebarStyle } = useSidebarNavigation();

  return (
    <Box
      sx={{
        ...boxContainerStyles,
        button: { padding: '0' },
        ...(sidebarStyle === 'mini' && { paddingLeft: '8px' }),
        ...(isExternal && { paddingRight: '20px' }),
        position: 'relative',
        '&:hover .icon-external, &:focus .icon-external': {
          opacity: '1',
        },
      }}
    >
      <LinkOrButton to={to} target={target} handleClick={handleClick}>
        {subMenu && <Typography sx={{ width: '20px' }}></Typography>}
        <SvgIcon component={icon} inheritViewBox sx={{ color: 'typography.main' }} />

        {text && (
          <Typography
            sx={{ textWrap: 'nowrap', color: 'typography.main' }}
            textTransform='capitalize'
          >
            {text}
          </Typography>
        )}

        {isExternal && (
          <Box
            className='icon-external'
            sx={{
              position: 'absolute',
              right: '0',
              transition: 'opacity .3s',
              opacity: '0',
            }}
          >
            <Tooltip
              placement='top'
              TransitionComponent={Zoom}
              title='This item will open in a new tab'
            >
              <LaunchIcon sx={{ color: 'typography.main', opacity: '.8' }} fontSize='small' />
            </Tooltip>
          </Box>
        )}
      </LinkOrButton>
    </Box>
  );
};

interface LinkOrButtonProps {
  to: string;
  target?: string;
  handleClick?: (() => void) | string | undefined;
  children: ReactNode;
}

export const LinkOrButton = ({ to, target, handleClick, children }: LinkOrButtonProps) => {
  if (to && to !== 'javascript:void(0);') {
    return (
      <StyledLink to={to} target={target}>
        {children}
      </StyledLink>
    );
  }

  return (
    <Button
      variant='text'
      onClick={typeof handleClick === 'function' ? handleClick : undefined}
      sx={{ color: 'typography.main' }}
    >
      {children}
    </Button>
  );
};

export default SidebarLink;
